import React, { useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';

export const ManageTemplates: React.FC = () => {
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Mock data for existing templates
  const existingTemplates = [
    { id: 1, name: 'Welcome Message', content: 'Welcome {NAME}! Thank you for joining us.', industry: 'general', usage: 245 },
    { id: 2, name: 'Appointment Reminder', content: 'Hi {NAME}, reminder of your appointment on {DATE}.', industry: 'healthcare', usage: 189 },
    { id: 3, name: 'Order Confirmation', content: 'Your order #{NUMBER6} has been confirmed!', industry: 'retail', usage: 567 },
  ];

  // Mock data for template recommendations
  const recommendedTemplates = [
    { 
      id: 1, 
      name: 'Event RSVP', 
      content: 'Hi {NAME}, please confirm your attendance for {EVENT} on {DATE}. Reply YES to confirm.',
      industry: 'events',
      useCase: 'Event Management',
      engagement: '85%'
    },
    { 
      id: 2, 
      name: 'Delivery Update', 
      content: 'Your package is out for delivery! Track here: {URL}',
      industry: 'logistics',
      useCase: 'Order Updates',
      engagement: '92%'
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex font-['Helvetica_Neue']">
      <div className="flex-1">
        {/* Top Banner */}
        <div className="bg-white border-b">
          <div className="px-6 py-4">
            <h1 className="text-xl font-bold">Manage Templates</h1>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6 max-w-7xl mx-auto">
          {/* Section 1: Adjust Existing Templates */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-bold">Adjust Existing Templates</h2>
              <button className="flex items-center bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E] transition-colors duration-200">
                <PlusIcon className="w-5 h-5 mr-2" />
                Create Template
              </button>
            </div>

            {/* Search and Filter */}
            <div className="flex gap-4 mb-6">
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
              />
              <select
                value={selectedIndustry}
                onChange={(e) => setSelectedIndustry(e.target.value)}
                className="w-48 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
              >
                <option value="all">All Industries</option>
                <option value="retail">Retail</option>
                <option value="healthcare">Healthcare</option>
                <option value="events">Events</option>
                <option value="logistics">Logistics</option>
              </select>
            </div>

            {/* Templates Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Content</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {existingTemplates.map((template) => (
                    <tr key={template.id}>
                      <td className="px-4 py-4 text-sm text-gray-900">{template.name}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{template.content}</td>
                      <td className="px-4 py-4 text-sm text-gray-900">{template.usage} times</td>
                      <td className="px-4 py-4">
                        <div className="flex space-x-2">
                          <button className="text-gray-600 hover:text-[#FF5722]">
                            <PencilIcon className="w-5 h-5" />
                          </button>
                          <button className="text-gray-600 hover:text-red-500">
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 2: Template Recommendations */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-bold mb-6">Template Recommendations and Examples</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {recommendedTemplates.map((template) => (
                <div key={template.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-medium text-gray-900">{template.name}</h3>
                      <p className="text-sm text-gray-500">{template.useCase}</p>
                    </div>
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">
                      {template.engagement} Engagement
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-4">{template.content}</p>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Industry: {template.industry}</span>
                    <div className="flex space-x-2">
                      <button className="flex items-center text-sm text-[#FF5722] hover:text-[#F4511E]">
                        <DocumentDuplicateIcon className="w-4 h-4 mr-1" />
                        Use Template
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageTemplates; 